import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "../lib/cn";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

export interface SearchInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Milliseconds to wait before reporting a change upstream. */
  debounceMs?: number;
  ariaLabel?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Debounced search box. Holds its own text so typing stays responsive while the
 * parent only re-queries once the user pauses.
 */
export function SearchInput({
  id,
  value,
  onChange,
  placeholder = "Search…",
  debounceMs = 350,
  ariaLabel = "Search",
  autoFocus = false,
  className,
}: SearchInputProps) {
  const [text, setText] = useState(value);
  const debounced = useDebouncedValue(text, debounceMs);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastReported = useRef(value);

  // Keep in step when the parent resets the query (e.g. "clear all filters").
  useEffect(() => {
    if (value !== lastReported.current) {
      lastReported.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (debounced !== lastReported.current) {
      lastReported.current = debounced;
      onChange(debounced);
    }
  }, [debounced, onChange]);

  return (
    <div className={cn("relative flex items-center", className)}>
      <Search
        size={16}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 text-content-subtle"
      />
      <input
        id={id}
        ref={inputRef}
        type="search"
        value={text}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(event) => setText(event.target.value)}
        className={cn(
          "h-10 w-full rounded-lg border border-line-strong bg-surface pl-9 pr-9 text-sm text-content",
          "placeholder:text-content-subtle transition-colors",
          "focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/40",
          "[&::-webkit-search-cancel-button]:appearance-none",
        )}
      />
      {text && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setText("");
            inputRef.current?.focus();
          }}
          className="absolute right-2 grid size-6 place-items-center rounded-md text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
